import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { t } from 'src/primitives/I18n';
import type { TreeState, RootMenuTriggerState } from 'react-stately';
import type { AriaMenuProps, AriaPopoverProps, PressEvent } from 'react-aria';
import type { Node as ReactAriaNode } from '@react-types/shared';
import { Section as ReactAriaSection, Item as ReactAriaItem, useTreeState, useMenuTriggerState, useSubmenuTriggerState } from 'react-stately';
import { useMenu, useMenuSection, useMenuTrigger, useSubmenuTrigger, useMenuItem, useLongPress, useButton, useFilter, useAutocomplete, useHover, useFocusRing, useFocusWithin, mergeProps, useObjectRef } from 'react-aria';
// NOTE - useAutocompleteState is only reachable via this private subpath in
// react-stately 3.48 (not re-exported from the top-level entry).
import { useAutocompleteState } from 'react-stately/private/autocomplete/useAutocompleteState';
import '../Consts.stylex';
import { Transitions } from '../Consts.stylex';
import '../theme/Colors.stylex';
import { ColorNeutral, ColorBrand } from '../theme/Colors.stylex';
import '../theme/Spaces.stylex';
import { Padding, Gap } from '../theme/Spaces.stylex';
import '../theme/Radius.stylex';
import { CornerRadius } from '../theme/Radius.stylex';
import '../theme/Fonts.stylex';
import { FontFamily, FontSize } from '../theme/Fonts.stylex';
import { Touchable } from '../../primitives/Touchable';
import { Popover } from '../overlays/Popover';
import { OverlayTriggerView, type Preset as OverlayTriggerPreset } from '../overlays/OverlayTriggerView';
import { Icon } from '../images-and-icons/Icon';
import { TextStyle } from '../typography/TextStyle';
import { InputSearch } from './InputSearch';
import { Checkbox, CheckboxTick } from './Checkbox';
import { make as CountBadge } from '../feedback-indicators/CountBadge.gen';
import { Spinner } from '../feedback-indicators/Spinner';

// NOTE: Built on useMenu subsystem (not useSelect) to support nested submenus

const SEARCHABLE_LEAF_THRESHOLD = 5;
const MENU_MAX_WIDTH = 320;
const MENU_MAX_HEIGHT = 310;

// MARK: data model
// ================
export type SelectItem = {
  key: string;
  label: string;
  items?: SelectItem[];
  value?: unknown;
  disabled?: boolean;
  // Node pinned to the row's end (space-between), e.g. a badge — a lighter
  // alternative to renderItem when you only need a trailing element.
  endElement?: React.ReactNode;
  // Mutually-exclusive option (e.g. "All", "Unclassified"): always mono-select
  // even in multiple mode, and shown with a tick instead of a checkbox.
  exclusive?: boolean;
};
export type SelectSection = {
  title?: string;
  items: SelectItem[];
};
export type RenderValueArgs = {
  selectedItems: SelectItem[];
  active: boolean;
  hovered: boolean;
  focused: boolean;
};
export type RenderItemArgs = {
  item: SelectItem;
  isSubmenuTrigger: boolean;
  isSelected: boolean;
  isFocused: boolean;
  isDisabled: boolean;
};
export type RenderTriggerArgs = RenderValueArgs;
export type SelectProps = {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultFoundText?: string;
  preset: OverlayTriggerPreset;
  sections: SelectSection[];
  selectedKeys?: string[];
  multiple?: boolean;
  searchable?: boolean;
  size?: 'normal' | 'compact';
  highlighted?: boolean;
  defaultOpen?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  onToggle?: (isOpen: boolean) => void;
  onSelectionChange: (keys: string[]) => void;
  // Multiple mode: whether two rows may stay checked at once. Called for the row just checked
  // against every other checked row; the incompatible ones are unchecked right away.
  shouldSelectTogether?: (key: string, otherKey: string) => boolean;
  renderTrigger?: (args: RenderTriggerArgs) => React.ReactNode;
  renderValue?: (args: RenderValueArgs) => React.ReactNode;
  renderItem?: (args: RenderItemArgs) => React.ReactNode;
  footerElement?: React.ReactNode;
};

// MARK: helpers
// =============
function findLabel(sections: SelectSection[], key: string | null | undefined): string | undefined {
  if (key == null) return undefined;
  const walk = (items: SelectItem[]): string | undefined => {
    for (const it of items) {
      if (it.key === key) return it.label;
      if (it.items) {
        const found = walk(it.items);
        if (found !== undefined) return found;
      }
    }
    return undefined;
  };
  return walk(sections.flatMap(s => s.items));
}

// Resolves selected keys back to their items (recurses into submenus)
function findItems(sections: SelectSection[], keys: Set<string>): SelectItem[] {
  const result: SelectItem[] = [];
  const walk = (items: SelectItem[]) => {
    for (const it of items) {
      if (keys.has(it.key)) result.push(it);
      if (it.items) walk(it.items);
    }
  };
  walk(sections.flatMap(s => s.items));
  return result;
}

// Builds react-stately collection elements; carries child data as `nsItems` for per-level filtering
function buildItem(item: SelectItem): React.ReactElement {
  const isParent = !!item.items && item.items.length > 0;
  const extra: any = {
    textValue: item.label,
    title: item.label
  };
  // Carry the original item so render props get the full data (value included)
  extra.nsItem = item;
  if (isParent) extra.nsItems = item.items;
  return <ReactAriaItem key={item.key} {...extra}>
      {(isParent ? item.items!.map(buildItem) : undefined) as any}
    </ReactAriaItem>;
}
function buildChildren(sections: SelectSection[]): React.ReactNode {
  // Drop empty sections so a titled-but-empty group (e.g. still loading, or fully
  // filtered out) doesn't render a dangling header.
  return sections.filter(section => section.items.length > 0).map((section, i) => <ReactAriaSection key={`section-${i}`} title={section.title}>
        {section.items.map(buildItem)}
      </ReactAriaSection>);
}
function countLeaves(sections: SelectSection[]): number {
  const walk = (items: SelectItem[]): number => items.reduce((acc, it) => acc + (it.items?.length ? walk(it.items) : 1), 0);
  return walk(sections.flatMap(s => s.items));
}

// Leaves the user can actually browse — excludes already-selected ones. A consumer may inject
// the selected item into the list purely to resolve the trigger label while the real page is
// still fetching; that placeholder must not count as content, or the loading spinner would be
// suppressed on open (there'd be "1 leaf" that is really just the current selection).
function countBrowsableLeaves(sections: SelectSection[], selectedKeys: Set<string>): number {
  const walk = (items: SelectItem[]): number => items.reduce((acc, it) => acc + (it.items?.length ? walk(it.items) : selectedKeys.has(it.key) ? 0 : 1), 0);
  return walk(sections.flatMap(s => s.items));
}
function collectLeafKeys(items: SelectItem[]): string[] {
  return items.flatMap(it => it.items?.length ? collectLeafKeys(it.items) : [it.key]);
}
// Keys of exclusive (mono-select) leaves across all levels
function collectExclusiveKeys(sections: SelectSection[]): Set<string> {
  const set = new Set<string>();
  const walk = (items: SelectItem[]) => {
    for (const it of items) {
      if (it.items?.length) walk(it.items);else if (it.exclusive) set.add(it.key);
    }
  };
  walk(sections.flatMap(s => s.items));
  return set;
}
// Keys of disabled items (leaves AND parents) across all levels
function collectDisabledKeys(sections: SelectSection[]): Set<string> {
  const set = new Set<string>();
  const walk = (items: SelectItem[]) => {
    for (const it of items) {
      if (it.disabled) set.add(it.key);
      if (it.items?.length) walk(it.items);
    }
  };
  walk(sections.flatMap(s => s.items));
  return set;
}
// Aggregate selection state for collapsed submenu parent
type AggState = 'none' | 'some' | 'all';
function subtreeSelection(items: SelectItem[], keys: Set<string>): {
  state: AggState;
  count: number;
} {
  const leaves = collectLeafKeys(items);
  const count = leaves.filter(k => keys.has(k)).length;
  const state: AggState = count === 0 ? 'none' : count === leaves.length ? 'all' : 'some';
  return {
    state,
    count
  };
}

// Centered spinner shown in the dropdown body while the initial page is fetching.
function LoadingBody() {
  return <div data-select-content="" className="minWidth-x1jzhcrs display-x78zum5 alignItems-x6s0dn4 justifyContent-xl56j7k padding-xdeice" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1638">
      <Spinner size={24} />
    </div>;
}
function NoResultsBody({
  text
}: {
  text: string;
}) {
  return <div data-select-content="" className="minWidth-x1jzhcrs padding-xb4tss4 fontFamily-x1q3ln6d fontSize-x1egw0ox fontStyle-x1k4tb9n color-xt4ixol textAlign-x2b8uid" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1629">
      {text}
    </div>;
}
type Contains = (a: string, b: string) => boolean;

// Separator between parent labels in a condensed-view section title (path)
const PATH_SEPARATOR = ' › ';

// Condensed search view: flattens the subtree to a single list.
// - a LEAF whose label matches is lifted up as a row; its section title is the
//   recursive path of its parents;
// - a PARENT whose OWN label matches is kept whole (still a submenu trigger, so
//   drill-down is preserved) WITHOUT exploding its non-matching leaves.
// Groups are emitted in first-appearance (DFS) order.
function flattenMatches(sections: SelectSection[], query: string, contains: Contains): SelectSection[] {
  const order: string[] = [];
  const groups = new Map<string, {
    title: string;
    items: SelectItem[];
  }>();
  const push = (path: string[], node: SelectItem) => {
    const groupKey = path.join(' ');
    let group = groups.get(groupKey);
    if (!group) {
      group = {
        title: path.join(PATH_SEPARATOR),
        items: []
      };
      groups.set(groupKey, group);
      order.push(groupKey);
    }
    group.items.push(node);
  };
  const walk = (items: SelectItem[], path: string[]) => {
    for (const it of items) {
      const nameMatches = contains(it.label, query);
      if (it.items?.length) {
        // Parent matched by name → keep whole (drill-down), don't explode children
        if (nameMatches) push(path, it);
        // A disabled parent prunes its whole subtree: it's unreachable by
        // navigation (no drill-down), so it stays unreachable in search too —
        // otherwise its (non-disabled) children would become selectable here.
        else if (!it.disabled) walk(it.items, [...path, it.label]);
      } else if (nameMatches || it.exclusive) {
        // Exclusive leaves (e.g. "All") always surface, regardless of the query
        push(path, it);
      }
    }
  };
  for (const s of sections) walk(s.items, s.title ? [s.title] : []);

  // Parent-less matches share the empty path → a single group. Keep the titled
  // groups in DFS order, then append the parent-less one LAST as a catch-all.
  // Only label it "Others" when titled groups coexist (otherwise it's the whole
  // flat list and needs no heading).
  const PARENT_LESS_KEY = '';
  const titledKeys = order.filter(k => k !== PARENT_LESS_KEY);
  const result: SelectSection[] = titledKeys.map(k => {
    const group = groups.get(k)!;
    return {
      title: group.title,
      items: group.items
    };
  });
  const parentLess = groups.get(PARENT_LESS_KEY);
  if (parentLess) {
    result.push({
      title: titledKeys.length > 0 ? t('Others') : undefined,
      items: parentLess.items
    });
  }
  return result;
}

// Threaded to every menu level
type MenuContext = {
  workingKeys: Set<string>;
  disabledKeys: Set<string>;
  multiple: boolean;
  additive: boolean; // true after first checkbox toggle this open
  condensed?: boolean; // true inside the flattened search view (drives path-title headings)
  hideIndicatorSlot?: boolean; // single mode: this level has no selection → drop the empty tick column
  bounded: boolean; // cap the menu width (filter preset) vs. let it fill (inputField)
  searchPlaceholder: string;
  noResultFoundText: string;
  rootTriggerState: RootMenuTriggerState;
  onRowSelect: (key: string) => void;
  onCheckboxToggle: (key: string) => void;
  onSubtreeToggle: (leafKeys: string[], select: boolean) => void;
  renderItem?: (args: RenderItemArgs) => React.ReactNode;
};

// MARK: MenuItem (leaf OR submenu-trigger row)
// ============================================
type MenuItemProps<T> = {
  item: ReactAriaNode<T>;
  state: TreeState<T>;
  ctx: MenuContext;
  submenuOpened?: boolean;
};
function MenuItemComponent<T extends object>(props: MenuItemProps<T>, ref: React.ForwardedRef<HTMLDivElement>) {
  const {
    item,
    state,
    ctx,
    submenuOpened,
    ...ariaProps
  } = props;
  const itemRef = useObjectRef(ref);
  const key = String(item.key);
  const isSubmenuTrigger = submenuOpened !== undefined;
  const nsItem = (item.props as any).nsItem as SelectItem | undefined;
  // Exclusive leaf (e.g. "All"): mono-select + tick even in multiple mode
  const isExclusive = !isSubmenuTrigger && !!nsItem?.exclusive;
  const {
    menuItemProps,
    isFocused,
    isPressed,
    isDisabled
  } = useMenuItem({
    ...ariaProps,
    key: item.key,
    closeOnSelect: false,
    onAction: isSubmenuTrigger ? undefined : () => ctx.onRowSelect(key)
  }, state, itemRef as React.RefObject<HTMLDivElement>);
  const {
    longPressProps
  } = useLongPress({
    isDisabled: isSubmenuTrigger || isExclusive || !ctx.multiple || isDisabled,
    onLongPress: () => ctx.onCheckboxToggle(key)
  });
  const isSelected = !isSubmenuTrigger && ctx.workingKeys.has(key);
  const isItemFocused = isFocused || submenuOpened === true;
  const textVariation = isDisabled ? {
    NAME: 'custom',
    VAL: ColorNeutral.textDisabled
  } as const : isSelected ? 'primary' // selected stays in brand, even while hovered
  : isItemFocused ? {
    NAME: 'custom',
    VAL: ColorNeutral.textAccentHover
  } as const : 'neutral';
  const textWeight = isSelected && !isDisabled ? 'semibold' : 'regular';

  // Submenu aggregate (for parent to surface hidden selections)
  const agg = isSubmenuTrigger ? subtreeSelection(((item.props as any).nsItems ?? []) as SelectItem[], ctx.workingKeys) : null;
  return <div ref={itemRef as any} {...mergeProps(menuItemProps, longPressProps)} role={!isSubmenuTrigger ? ctx.multiple ? 'menuitemcheckbox' : 'menuitemradio' : menuItemProps.role} aria-checked={!isSubmenuTrigger ? isSelected : menuItemProps['aria-checked']}
  // Ctrl/Cmd+Enter on a leaf = select + arm additive mode (like clicking the checkbox).
  // Capture phase intercepts before react-aria's usePress fires the default single-select
  // action on the bubble phase. Works in both real-focus and virtual-focus (searchable) menus.
  onKeyDownCapture={!isSubmenuTrigger && !isExclusive && ctx.multiple && !isDisabled ? event => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      event.stopPropagation();
      ctx.onCheckboxToggle(key);
    }
  } : undefined} {...menuItemStyleProps({
    focused: isItemFocused,
    selected: isSelected,
    multiple: ctx.multiple,
    pressed: isPressed && !submenuOpened,
    disabled: isDisabled,
    defaultCursor: ctx.additive && !isSubmenuTrigger && !isExclusive
  })}>
      {ctx.multiple && isSubmenuTrigger ?
    // Multi mode submenu parent: active checkbox toggles all descendant leaves at once
    <span className="display-x78zum5 flex-x1i6dltt height-x5yr21d alignItems-x6s0dn4 justifyContent-xl56j7k" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1563">
          <Checkbox checked={agg!.state === 'all'} indeterminate={agg!.state === 'some'} disabled={isDisabled} onChange={(next: boolean) => ctx.onSubtreeToggle(collectLeafKeys(((item.props as any).nsItems ?? []) as SelectItem[]), next)} />
        </span> : ctx.multiple && !isExclusive ? <span className="display-x78zum5 flex-x1i6dltt height-x5yr21d alignItems-x6s0dn4 justifyContent-xl56j7k" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1563">
          <Checkbox checked={isSelected} hovered={ctx.additive && isFocused} disabled={isDisabled} onChange={() => ctx.onCheckboxToggle(key)} />
        </span> : ctx.hideIndicatorSlot ? null : isSubmenuTrigger ?
    // Single mode, level with no selection at all: drop the empty tick column
    // Single mode submenu parent: grey pastille when a descendant is selected
    // (the tick stays reserved for the selected leaf itself)
    <span aria-hidden className="display-x78zum5 flex-x1i6dltt height-x5yr21d alignItems-x6s0dn4 justifyContent-xl56j7k" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1563">
          {agg!.count > 0 && <span className="width-x4nz7rj height-x14v0luk borderRadius-x16rqkct backgroundColor-xnmrsfa" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1557" />}
        </span> :
    // Single mode leaf or exclusive leaf: tick
    <span aria-hidden className="display-x78zum5 flex-x1i6dltt height-x5yr21d alignItems-x6s0dn4 justifyContent-xl56j7k" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1563">
          <CheckboxTick displayed={isSelected} />
        </span>}
      <span className="flex-x98rzlu minWidth-xeuugli overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1550">
        {(() => {
        const custom = nsItem ? ctx.renderItem?.({
          item: nsItem,
          isSubmenuTrigger,
          isSelected,
          isFocused: isItemFocused,
          isDisabled
        }) : undefined;
        return <TextStyle size="small" lineHeight="medium" variation={textVariation} weight={textWeight} maxLines={2}>
              {custom != null ? custom : (item.props as any).title ?? item.textValue}
            </TextStyle>;
      })()}
      </span>
      {nsItem?.endElement != null && <span className="display-x78zum5 flex-x3psx0u alignItems-x6s0dn4 paddingLeft-x1vvg5pe" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1570">{nsItem.endElement}</span>}
      {isSubmenuTrigger && ctx.multiple && agg!.count > 0 && <span className="fontFamily-x1q3ln6d fontSize-x1egw0ox color-xt4ixol" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1576">{agg!.count}</span>}
      {isSubmenuTrigger && <div className="display-x78zum5 marginRight-x1bxrdft" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1581">
          <Icon name="chevron_right" bold={submenuOpened} width={14} height={14} fill={isDisabled ? ColorNeutral.iconDisabled : isPressed && !submenuOpened ? ColorNeutral.iconPressed : isItemFocused ? ColorNeutral.iconAccentHover : ColorNeutral.iconAccent} />
        </div>}
    </div>;
}
const MenuItem = React.forwardRef(MenuItemComponent) as <T extends object>(props: MenuItemProps<T> & {
  ref?: React.ForwardedRef<HTMLDivElement>;
}) => React.ReactElement;

// MARK: SubmenuItem (adapted from MenuActions.tsx)
// ================================================
type SubmenuItemProps<T> = {
  item: ReactAriaNode<T>;
  state: TreeState<T>;
  ctx: MenuContext;
  parentMenuRef: React.RefObject<HTMLDivElement>;
};
function SubmenuItemComponent<T extends object>(props: SubmenuItemProps<T>) {
  const {
    item,
    state,
    ctx,
    parentMenuRef
  } = props;
  const triggerRef = React.useRef(null);
  const submenuRef = React.useRef<HTMLDivElement>(null);
  const submenuTriggerState = useSubmenuTriggerState({
    triggerKey: item.key
  }, ctx.rootTriggerState);
  const {
    submenuTriggerProps: menuItemAriaProps,
    submenuProps: submenuAriaProps,
    popoverProps: popoverAriaProps
  } = useSubmenuTrigger({
    parentMenuRef,
    submenuRef,
    delay: 200
  }, submenuTriggerState, triggerRef);
  const closeAllOnKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') submenuTriggerState.closeAll();
  };
  const mergedTriggerProps = mergeProps(menuItemAriaProps, {
    onKeyDown: closeAllOnKeyDown
  });
  const mergedSubmenuProps = mergeProps(submenuAriaProps, {
    onKeyDown: closeAllOnKeyDown
  });

  // Child data carried on node by buildItem — drives fresh per-level filtering
  const childItems = ((item.props as any).nsItems ?? []) as SelectItem[];

  // A disabled parent stays a (greyed) trigger row but can't be drilled into:
  // drop the trigger/hover props and never mount the submenu popover.
  const isDisabled = ctx.disabledKeys.has(String(item.key));
  const isOpen = !isDisabled && submenuTriggerState.isOpen;
  return <>
      <MenuItem {...isDisabled ? {} : mergedTriggerProps} key={item.key} item={item} state={state} ctx={ctx} submenuOpened={isOpen} ref={triggerRef} />
      {isOpen && <Popover ariaProps={popoverAriaProps as AriaPopoverProps} placement="end top" offset={5} crossOffset={-9} shouldUpdatePosition={false} state={submenuTriggerState} triggerRef={triggerRef}>
          <MenuLevel sections={[{
        items: childItems
      }]} ctx={ctx} label={(item.props as any).title ?? 'submenu'} menuAriaProps={mergedSubmenuProps} menuRef={submenuRef} />
        </Popover>}
    </>;
}
const SubmenuItem = React.memo(SubmenuItemComponent);

// MARK: MenuSection
// =================
type MenuSectionProps<T> = {
  item: ReactAriaNode<T>;
  state: TreeState<T>;
  ctx: MenuContext;
  parentMenuRef: React.RefObject<HTMLDivElement>;
};
function MenuSectionComponent<T extends object>(props: MenuSectionProps<T>) {
  const {
    item,
    state,
    ctx,
    parentMenuRef
  } = props;
  const {
    itemProps,
    groupProps,
    headingProps
  } = useMenuSection({
    heading: item.rendered,
    'aria-label': item['aria-label']
  });
  return <>
      <li {...itemProps} className="listStyle-xe8uvvx" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1585">
        {item.rendered && <span {...headingProps} className="display-x78zum5 boxSizing-x9f619 height-x10w6t97 alignItems-x6s0dn4 paddingInline-xq8m5sq" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1593">
            {ctx.condensed ? <span className="minWidth-xeuugli maxWidth-x193iq5w direction-xzyj77d textAlign-xdpxx8g overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft transform-x1bnzur" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1604">
                <TextStyle size="xxsmall" variation="normal" wrap={false}>
                  {item.rendered}
                </TextStyle>
              </span> : <span className="display-x3nfvp2 transform-xwnhlry" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1600">
                <TextStyle size="xxsmall" variation="normal" wrap={false}>
                  {item.rendered}
                </TextStyle>
              </span>}
          </span>}
        <ul {...groupProps} className="display-x78zum5 flexDirection-xdt5ytf margin-x1ghz6dp padding-x1717udv listStyle-xe8uvvx" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1586">
          {[...item.childNodes].map(child => child.hasChildNodes ? <SubmenuItem key={child.key} item={child} state={state} ctx={ctx} parentMenuRef={parentMenuRef} /> : <MenuItem key={child.key} item={child} state={state} ctx={ctx} />)}
        </ul>
      </li>
    </>;
}
const MenuSection = React.memo(MenuSectionComponent) as typeof MenuSectionComponent;

// MARK: Menu
// ==========
// Tracks whether the scroll container overflows above/below the viewport, to
// drive the fade gradients. Recomputes on scroll, on resize, and whenever the
// `contentKey` changes (search-filtering grows/shrinks content without emitting
// a scroll or resize event on the fixed-height container).
// NOTE: recompute is deliberately keyed on contentKey rather than run every
// render — an unconditional layout effect that setState's would loop against
// the autocomplete's virtual-focus re-renders (max update depth exceeded).
function useScrollEdges(ref: React.RefObject<HTMLElement>, contentKey: unknown) {
  const [edges, setEdges] = React.useState({
    top: false,
    bottom: false
  });
  const update = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const {
      scrollTop,
      clientHeight,
      scrollHeight
    } = el;
    setEdges(prev => {
      const top = scrollTop > 0;
      const bottom = scrollTop + clientHeight < scrollHeight - 1;
      return prev.top === top && prev.bottom === bottom ? prev : {
        top,
        bottom
      };
    });
  }, [ref]);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, {
      passive: true
    });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [update]);
  React.useLayoutEffect(update, [update, contentKey]);
  return edges;
}
type MenuProps<T> = AriaMenuProps<T> & {
  ctx: MenuContext;
  // Root-level only: fired when scrolled near the bottom (infinite pagination).
  onLoadMore?: () => void;
};
function MenuComponent<T extends object>(props: MenuProps<T>, ref: React.ForwardedRef<HTMLDivElement> | null) {
  const {
    ctx,
    onLoadMore,
    ...rootAriaProps
  } = props;
  const state = useTreeState({
    ...rootAriaProps,
    children: props.children,
    disabledKeys: ctx.disabledKeys
  });
  const menuRef = useObjectRef(ref);
  const {
    menuProps
  } = useMenu({
    ...rootAriaProps,
    shouldFocusWrap: false
  }, state, menuRef as React.RefObject<HTMLDivElement>);
  const edges = useScrollEdges(menuRef as React.RefObject<HTMLDivElement>, state.collection.size);

  // Infinite pagination: fire onLoadMore when scrolled within 100px of the bottom.
  // The consumer guards against duplicate/terminal calls.
  React.useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el || !onLoadMore) return;
    const onScroll = () => {
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) onLoadMore();
    };
    el.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => el.removeEventListener('scroll', onScroll);
  }, [onLoadMore]);

  // Single mode: if no item in THIS level surfaces an indicator (no selected leaf,
  // no parent with a selected descendant), drop the tick column so rows aren't
  // left-padded by an empty slot. Multi mode always keeps the checkbox column.
  const levelHasIndicator = (nodes: Iterable<ReactAriaNode<T>>): boolean => {
    for (const node of nodes) {
      if (node.type === 'section') {
        if (levelHasIndicator(node.childNodes)) return true;
      } else if (node.hasChildNodes) {
        const nsItems = ((node.props as any)?.nsItems ?? []) as SelectItem[];
        if (subtreeSelection(nsItems, ctx.workingKeys).count > 0) return true;
      } else if (ctx.workingKeys.has(String(node.key))) {
        return true;
      }
    }
    return false;
  };
  const hideIndicatorSlot = !ctx.multiple && !levelHasIndicator(state.collection);
  // Set explicitly (not inherited) so a nested level with its own selection isn't
  // hidden by an ancestor's flag. Memoized to keep child memoization intact.
  const levelCtx = React.useMemo(() => ({
    ...ctx,
    hideIndicatorSlot
  }), [ctx, hideIndicatorSlot]);

  // On open, focus + scroll this level's selected item into view (mirrors Select.res, which
  // relies on react-aria's autoFocus-to-selected — unavailable here since selection lives in
  // ctx.workingKeys, not the selection manager). Runs once per menu level mount.
  React.useEffect(() => {
    const selectedKey = [...state.collection.getKeys()].find(k => ctx.workingKeys.has(String(k)));
    if (selectedKey == null) return;
    const el = menuRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(String(selectedKey))}"]`);
    if (!el) return;
    state.selectionManager.setFocused(true);
    state.selectionManager.setFocusedKey(selectedKey);
    el.scrollIntoView({
      block: 'nearest'
    });
    // Move real DOM focus unless a search input owns it (virtual-focus / searchable menus)
    if (document.activeElement?.tagName !== 'INPUT') el.focus();
  }, []);
  return <div className="position-x1n2onr6 display-x78zum5 minHeight-x2lwn1j" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1476">
      <div {...menuProps} ref={menuRef as React.RefObject<HTMLDivElement>} {...{
      0: {
        className: "flexGrow-x1iyjqo2 maxHeight-xyf3nwv overflowY-x1odjw0f padding-x170jadj scrollPaddingBlock-xmxsqhg outline-x1a2a7pz",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1502"
      },
      1: {
        className: "flexGrow-x1iyjqo2 maxHeight-xyf3nwv overflowY-x1odjw0f padding-x170jadj scrollPaddingBlock-xmxsqhg outline-x1a2a7pz maxWidth-x1va8c73",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1502; wino-gc:src/resources/selection-and-input/Select.tsx:1510"
      }
    }[!!ctx.bounded << 0]}>
        {[...state.collection].map(item => item.type === 'section' ? <MenuSection key={item.key} item={item} state={state} ctx={levelCtx} parentMenuRef={menuRef as React.RefObject<HTMLDivElement>} /> : item.hasChildNodes ? <SubmenuItem key={item.key} item={item} state={state} ctx={levelCtx} parentMenuRef={menuRef as React.RefObject<HTMLDivElement>} /> : <MenuItem key={item.key} item={item} state={state} ctx={levelCtx} />)}
      </div>
      <div aria-hidden {...{
      0: {
        className: "position-x10l6tqk left-xu96u03 right-x3m8u43 height-x18gnavp zIndex-xhtitgo pointerEvents-x47corl opacity-xg01cxk transition-x1c1w39i top-x13vifvy backgroundImage-x1gax4bi",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1481; wino-gc:src/resources/selection-and-input/Select.tsx:1494"
      },
      1: {
        className: "position-x10l6tqk left-xu96u03 right-x3m8u43 height-x18gnavp zIndex-xhtitgo pointerEvents-x47corl transition-x1c1w39i top-x13vifvy backgroundImage-x1gax4bi opacity-x1hc1fzr",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1481; wino-gc:src/resources/selection-and-input/Select.tsx:1494; wino-gc:src/resources/selection-and-input/Select.tsx:1491"
      }
    }[!!edges.top << 0]} />
      <div aria-hidden {...{
      0: {
        className: "position-x10l6tqk left-xu96u03 right-x3m8u43 height-x18gnavp zIndex-xhtitgo pointerEvents-x47corl opacity-xg01cxk transition-x1c1w39i bottom-x1ey2m1c backgroundImage-x1ujbsab",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1481; wino-gc:src/resources/selection-and-input/Select.tsx:1498"
      },
      1: {
        className: "position-x10l6tqk left-xu96u03 right-x3m8u43 height-x18gnavp zIndex-xhtitgo pointerEvents-x47corl transition-x1c1w39i bottom-x1ey2m1c backgroundImage-x1ujbsab opacity-x1hc1fzr",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1481; wino-gc:src/resources/selection-and-input/Select.tsx:1498; wino-gc:src/resources/selection-and-input/Select.tsx:1491"
      }
    }[!!edges.bottom << 0]} />
    </div>;
}
const ForwardedMenuComponent = React.forwardRef(MenuComponent);
const Menu = React.memo(ForwardedMenuComponent) as typeof ForwardedMenuComponent;
type MenuAriaProps = Record<string, any>;

// MARK: MenuLevel
// ===============
type MenuLevelProps = {
  sections: SelectSection[];
  ctx: MenuContext;
  label: string;
  menuAriaProps: MenuAriaProps;
  menuRef?: React.RefObject<HTMLDivElement>;
  searchable?: boolean;
  // Root-level async props (submenus never pass these).
  loading?: boolean;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
};
function MenuLevel(props: MenuLevelProps) {
  const {
    sections,
    ctx,
    label,
    menuAriaProps,
    menuRef,
    loading,
    onSearch,
    onLoadMore
  } = props;
  const searchable = props.searchable ?? countLeaves(sections) > SEARCHABLE_LEAF_THRESHOLD;
  if (searchable) {
    return <SearchableMenu sections={sections} ctx={ctx} label={label} menuAriaProps={menuAriaProps} menuRef={menuRef} loading={loading} onSearch={onSearch} onLoadMore={onLoadMore} />;
  }
  // Non-searchable initial load (no browsable item yet) → spinner instead of an empty menu.
  if (loading && countBrowsableLeaves(sections, ctx.workingKeys) === 0) {
    return <LoadingBody />;
  }
  if (countLeaves(sections) === 0) {
    return <NoResultsBody text={ctx.noResultFoundText} />;
  }
  return <Menu {...menuAriaProps} aria-label={label} ctx={ctx} onLoadMore={onLoadMore} ref={menuRef as any}>
      {buildChildren(sections) as any}
    </Menu>;
}

// Server mode only: drops SELECTED, non-exclusive leaves whose label doesn't match the query.
// The consumer keeps the selected item in `sections` so the trigger label still resolves, but a
// selected item that doesn't match the active search shouldn't linger in the results list.
// Exclusive items (e.g. "All") are always kept. Server results are matched server-side (possibly
// on fields absent from the label), so only the selected placeholder is filtered — never the rest.
function pruneStaleSelected(sections: SelectSection[], query: string, contains: Contains, selectedKeys: Set<string>): SelectSection[] {
  const keep = (it: SelectItem): boolean => {
    const isLeaf = !it.items?.length;
    return !(isLeaf && selectedKeys.has(it.key) && !it.exclusive && !contains(it.label, query));
  };
  const prune = (items: SelectItem[]): SelectItem[] => items.filter(keep).map(it => it.items?.length ? {
    ...it,
    items: prune(it.items)
  } : it);
  return sections.map(s => ({
    ...s,
    items: prune(s.items)
  }));
}

// MARK: SearchableMenu
// ====================
type SearchableMenuProps = {
  sections: SelectSection[];
  ctx: MenuContext;
  label: string;
  menuAriaProps: MenuAriaProps;
  menuRef?: React.RefObject<HTMLDivElement>;
  loading?: boolean;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
};
function SearchableMenu(props: SearchableMenuProps) {
  const {
    sections,
    ctx,
    label,
    menuAriaProps,
    menuRef,
    loading,
    onSearch,
    onLoadMore
  } = props;
  // Server-driven search: the consumer returns already-filtered sections.
  const serverMode = onSearch != null;
  const autocompleteState = useAutocompleteState({});
  const inputRef = React.useRef<HTMLInputElement>(null);
  const collectionRef = React.useRef<HTMLDivElement>(null);
  const {
    contains
  } = useFilter({
    sensitivity: 'base'
  });
  const {
    inputProps,
    collectionProps,
    collectionRef: mergedCollectionRef
  } = useAutocomplete({
    inputRef,
    collectionRef
  }, autocompleteState);
  const query = autocompleteState.inputValue;
  // Client mode → empty query keeps the arborescence, else the flattened condensed view.
  // Server mode → results are already filtered; only prune the consumer-injected selected item
  // when it doesn't match the active query (keeps the trigger label resolvable via `sections`).
  const filteredSections = React.useMemo(() => {
    if (!query) return sections;
    if (!serverMode) return flattenMatches(sections, query, contains);
    return pruneStaleSelected(sections, query, contains, ctx.workingKeys);
  }, [serverMode, sections, query, contains, ctx.workingKeys]);
  // Condensed view drives path-title (left-ellipsis) section headings (client mode only)
  const menuCtx = !serverMode && query ? {
    ...ctx,
    condensed: true
  } : ctx;

  // Server mode: forward the query on each user change. Skip the initial mount so reopening
  // the dropdown doesn't refetch page 1 unprompted. Consumer owns debounce + pagination reset.
  const didForwardMount = React.useRef(false);
  React.useEffect(() => {
    if (!serverMode) return;
    if (!didForwardMount.current) {
      didForwardMount.current = true;
      return;
    }
    onSearch!(query);
  }, [serverMode, query]);

  // react-aria's autocomplete inputProps.onChange is value-based (onChange(value: string)),
  // intended for a react-aria-managed input. InputSearch renders a native <input>, whose
  // onChange fires with a SyntheticEvent — so we bridge it back to the value the state expects.
  const {
    onChange: onInputValueChange,
    ...restInputProps
  } = inputProps as any;
  const domInputProps = {
    ...restInputProps,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => onInputValueChange(event.target.value)
  };

  // Real DOM focus on search input (menu uses virtual focus)
  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Stable submenuRef for safe-triangle (avoid re-creating merged ref each render)
  React.useLayoutEffect(() => {
    if (menuRef) {
      ;
      (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = collectionRef.current;
    }
  });
  const browsableLeafCount = countBrowsableLeaves(filteredSections, ctx.workingKeys);
  return <div data-select-content="">
      <div className="paddingTop-x4p5aij borderBottomWidth-xso031l borderBottomStyle-x1q0q8m5 borderBottomColor-x10m8i1d" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1614">
        <InputSearch loading={(loading && browsableLeafCount > 0) ?? false} bordered={false} placeholder={ctx.searchPlaceholder} inputRef={inputRef} ariaProps={{
        ...domInputProps,
        'aria-label': ctx.searchPlaceholder
      } as React.ComponentProps<typeof InputSearch>['ariaProps']} onRequestClear={() => autocompleteState.setInputValue('')} />
      </div>
      {loading && browsableLeafCount === 0 ? <LoadingBody /> : countLeaves(filteredSections) === 0 ? <NoResultsBody text={ctx.noResultFoundText} /> : <Menu {...mergeProps(menuAriaProps, collectionProps) as any} aria-label={label} ctx={menuCtx} onLoadMore={onLoadMore} ref={mergedCollectionRef as any}>
          {buildChildren(filteredSections) as any}
        </Menu>}
    </div>;
}

// MARK: FooterAction
// ===================
// Convenience row to drop into `footerElement` (e.g. "Create …"). The dropdown wraps
// `footerElement` with its own padding/border, so this only renders the row itself.
export type FooterActionProps = {
  label: string;
  href?: string;
  target?: string;
  onPress?: (event: PressEvent) => void;
};
export function FooterAction(props: FooterActionProps) {
  const {
    label,
    href,
    target,
    onPress
  } = props;
  const {
    hoverProps,
    isHovered
  } = useHover({});
  const {
    isFocusVisible,
    focusProps
  } = useFocusRing();
  const active = isHovered || isFocusVisible;
  return <div {...mergeProps(hoverProps, focusProps)}>
      <Touchable href={href} onPress={onPress} ariaProps={target ? {
      target
    } : undefined}>
        <span className="display-x78zum5 width-xh8yej3 alignItems-x6s0dn4 justifyContent-x1qughib gap-xw718zh textDecoration-x1hl2dhg" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1645">
          <TextStyle size="small" lineHeight="medium" weight="semibold" variation={{
          NAME: 'custom',
          VAL: active ? ColorBrand.textDefault : ColorNeutral.textAccent
        }}>
            {label}
          </TextStyle>
          <Icon name="plus_small" width={16} height={16} bold fill={active ? ColorBrand.iconDefault : ColorNeutral.iconAccent} />
        </span>
      </Touchable>
    </div>;
}

// MARK: root
// ==========
export function Select(props: SelectProps) {
  const {
    label,
    placeholder = t('Select'),
    searchPlaceholder = t('Search'),
    noResultFoundText = t('No result found'),
    preset,
    sections,
    multiple = false,
    size,
    highlighted = false,
    defaultOpen = false,
    disabled = false,
    loading,
    onSearch,
    onLoadMore,
    onToggle,
    onSelectionChange,
    shouldSelectTogether,
    renderValue,
    renderTrigger,
    renderItem,
    footerElement
  } = props;
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const triggerState = useMenuTriggerState({
    defaultOpen
  });
  const {
    menuTriggerProps,
    menuProps
  } = useMenuTrigger({}, triggerState, triggerRef);
  // Closing from the trigger in non-modal mode.
  //
  // Non-modal Popover renders no FocusScope (shouldContainFocus=false), so react-aria
  // does NOT restore focus to the trigger on close — it only does so on the menu's own
  // close paths (Escape, selection). A trigger re-press hits two react-aria behaviours
  // that fight each other: usePopover's hardcoded shouldCloseOnBlur closes the popover
  // (focus leaving it), then the trigger's native onPressStart toggles it back open.
  //
  // So we own the "close from trigger" path: close explicitly, restore focus to the
  // trigger ourselves (mirroring the menu's other close paths, keeping isFocusWithin
  // true so keyboard reopen works), and swallow the native toggle to avoid the reopen.
  // Keyboard open is untouched — it flows through onKeyDown, not onPressStart.
  const closeFromTrigger = () => {
    triggerState.close();
    triggerRef.current?.focus();
  };
  const guardedTriggerProps = {
    ...menuTriggerProps,
    onPressStart: (event: any) => {
      if (triggerState.isOpen) return closeFromTrigger();
      (menuTriggerProps as any).onPressStart?.(event);
    },
    onPress: (event: any) => {
      if (event.pointerType === 'touch' && triggerState.isOpen) {
        return closeFromTrigger();
      }
      ;
      (menuTriggerProps as any).onPress?.(event);
    }
  };
  // menuTriggerProps use press abstraction; route through useButton for clickable button
  const {
    buttonProps
  } = useButton({
    ...guardedTriggerProps,
    isDisabled: disabled
  }, triggerRef);
  const valueId = React.useId();

  // Notify open/close, skipping the initial mount (mirrors the old Select's onToggle).
  const didToggleMount = React.useRef(false);
  React.useEffect(() => {
    if (!didToggleMount.current) {
      didToggleMount.current = true;
      return;
    }
    onToggle?.(triggerState.isOpen);
  }, [triggerState.isOpen]);
  const {
    hoverProps,
    isHovered
  } = useHover({});
  const [isFocusWithin, setFocusWithin] = React.useState(false);
  const {
    focusWithinProps
  } = useFocusWithin({
    onFocusWithinChange: setFocusWithin
  });

  // Working copy; snapshotted on open, committed on close (commit-on-close)
  const [workingKeys, setWorkingKeys] = React.useState<Set<string>>(() => new Set(props.selectedKeys ?? []));
  // Ref mirror for close effect
  const workingRef = React.useRef(workingKeys);
  workingRef.current = workingKeys;
  const wasOpen = React.useRef(false);
  // Becomes true after first checkbox toggle this open; reset each open
  const [additive, setAdditive] = React.useState(false);
  React.useEffect(() => {
    if (triggerState.isOpen) {
      if (!wasOpen.current) {
        setWorkingKeys(new Set(props.selectedKeys ?? []));
        setAdditive(false);
      }
      wasOpen.current = true;
    } else if (wasOpen.current) {
      wasOpen.current = false;
      onSelectionChange(Array.from(workingRef.current));
    }
  }, [triggerState.isOpen]);

  // NOTE - Close on outside mousedown (avoids unreliable shouldCloseOnInteractOutside with nested submenus)
  // > We can't rely on react-aria's interact-outside here: usePopover
  // > hardcodes shouldCloseOnBlur, and our modal root treats focus moving into the
  // > (non-modal, portaled) submenu as a blur — closing everything the moment a
  // > searchable submenu autofocuses its input.
  //
  // Capture phase is required: react-aria interactive elements (another Select,
  // any usePress button) stopPropagation on mousedown during bubbling, which would
  // hide the event from a bubble-phase listener. When a submenu is open, react-aria's
  // own pointer-level interact-outside still fires and closes just the submenu — so a
  // bubble listener would leave the root open (only the submenu closes). Capturing
  // runs before that stopPropagation, so the whole thing closes uniformly.
  React.useEffect(() => {
    if (!triggerState.isOpen) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      // The trigger is handled by onPressStart (close + focus). Skip it here:
      // in capture phase this listener also sees the *opening* click's mousedown
      // (mounted synchronously between pointerdown and mousedown), which would
      // otherwise close the menu the instant it opens.
      if (target && triggerRef.current?.contains(target)) return;
      const inside = target?.closest('[role="menu"], [data-select-content]');
      if (!inside) triggerState.close();
    };
    document.addEventListener('mousedown', handleMouseDown, true);
    return () => document.removeEventListener('mousedown', handleMouseDown, true);
  }, [triggerState.isOpen]);
  const exclusiveKeys = React.useMemo(() => collectExclusiveKeys(sections), [sections]);
  const disabledKeys = React.useMemo(() => collectDisabledKeys(sections), [sections]);

  // Keys the consumer rules out alongside the ones just checked. The added keys always win, so a
  // row never rejects itself.
  const pruneIncompatible = (keys: Set<string>, added: string[]) => {
    if (!shouldSelectTogether) return keys;
    const next = new Set(keys);
    for (const key of [...next]) {
      if (added.includes(key)) continue;
      if (!added.every(addedKey => shouldSelectTogether(addedKey, key))) next.delete(key);
    }
    return next;
  };

  // Toggle a normal (non-exclusive) key; picking one drops any exclusive selection
  const toggleNormalKey = (key: string) => setWorkingKeys(prev => {
    const next = new Set(prev);
    for (const k of exclusiveKeys) next.delete(k);
    if (next.has(key)) {
      next.delete(key);
      return next;
    }
    next.add(key);
    return pruneIncompatible(next, [key]);
  });
  const ctx: MenuContext = {
    workingKeys,
    disabledKeys,
    multiple,
    additive,
    bounded: preset === 'filter',
    searchPlaceholder,
    noResultFoundText,
    // Row-body click. Exclusive item: always mono-select + close (terminal, even in additive mode).
    // Otherwise: additive → toggle, else select-only + close
    onRowSelect: key => {
      if (multiple && exclusiveKeys.has(key)) {
        setWorkingKeys(new Set([key]));
        triggerState.close();
      } else if (additive) {
        toggleNormalKey(key);
      } else {
        setWorkingKeys(new Set([key]));
        triggerState.close();
      }
    },
    // Checkbox click: toggle + arm additive mode
    onCheckboxToggle: key => {
      setAdditive(true);
      toggleNormalKey(key);
    },
    // Submenu parent checkbox: select/deselect all its leaves at once (+ arm additive mode)
    onSubtreeToggle: (leafKeys, select) => {
      setAdditive(true);
      setWorkingKeys(prev => {
        const next = new Set(prev);
        for (const k of exclusiveKeys) next.delete(k);
        for (const k of leafKeys) {
          if (select) next.add(k);else next.delete(k);
        }
        return select ? pruneIncompatible(next, leafKeys) : next;
      });
    },
    rootTriggerState: triggerState,
    renderItem
  };

  // Trigger summary always reflects committed selection (commit-on-close):
  // the working set stays internal until the menu closes.
  const displayKeys = new Set(props.selectedKeys ?? []);
  const selectedItems = findItems(sections, displayKeys);
  const hasSelection = displayKeys.size > 0;
  // Default value (when no renderValue): multi-selection shows a CountBadge;
  // single/empty shows a plain label string (ellipsized).
  const isCountBadge = multiple && displayKeys.size > 1;
  return <div {...focusWithinProps} {...{
    0: {
      className: "display-x3nfvp2 flexDirection-xdt5ytf flexGrow-x1iyjqo2",
      "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1442"
    },
    1: {
      className: "display-x3nfvp2 flexDirection-xdt5ytf flexGrow-x1c4vz4f",
      "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1442; wino-gc:src/resources/selection-and-input/Select.tsx:1447"
    }
  }[!!(preset === 'filter') << 0]}>
      <button {...mergeProps(buttonProps, hoverProps)} aria-label={label}
    // No value node is rendered in `renderTrigger` mode, so there is nothing to point
    // at — the accessible name falls back to `aria-label` (the field name).
    aria-labelledby={renderTrigger ? undefined : valueId} ref={triggerRef} {...{
      0: {
        className: "display-x78zum5 width-xh8yej3 padding-x1717udv cursor-x1ypdohk textAlign-x1yc453h",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1450"
      },
      2: {
        className: "display-x78zum5 width-xh8yej3 padding-x1717udv cursor-x1ypdohk textAlign-x1yc453h maxWidth-x1va8c73",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1450; wino-gc:src/resources/selection-and-input/Select.tsx:1459"
      },
      1: {
        className: "display-x78zum5 width-xh8yej3 padding-x1717udv textAlign-x1yc453h cursor-xt0e3qv",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1450; wino-gc:src/resources/selection-and-input/Select.tsx:1653"
      },
      3: {
        className: "display-x78zum5 width-xh8yej3 padding-x1717udv textAlign-x1yc453h maxWidth-x1va8c73 cursor-xt0e3qv",
        "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1450; wino-gc:src/resources/selection-and-input/Select.tsx:1459; wino-gc:src/resources/selection-and-input/Select.tsx:1653"
      }
    }[!!(preset === 'filter') << 1 | !!disabled << 0]}>
        {renderTrigger ? renderTrigger({
        selectedItems,
        active: triggerState.isOpen,
        hovered: isHovered,
        focused: isFocusWithin
      }) : <OverlayTriggerView preset={preset} label={label} size={size} highlighted={highlighted} disabled={disabled} hovered={isHovered} active={triggerState.isOpen} focused={isFocusWithin}>
            <span id={valueId} {...{
          0: {
            className: "display-x78zum5 minWidth-xeuugli alignItems-x6s0dn4",
            "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1462"
          },
          1: {
            className: "display-x78zum5 minWidth-xeuugli alignItems-x6s0dn4 color-x1wvqapn",
            "data-style-src": "wino-gc:src/resources/selection-and-input/Select.tsx:1462; wino-gc:src/resources/selection-and-input/Select.tsx:1473"
          }
        }[!!(!renderValue && !hasSelection) << 0]}>
              {renderValue ? renderValue({
            selectedItems,
            active: triggerState.isOpen,
            hovered: isHovered,
            focused: isFocusWithin
          }) : isCountBadge ? <CountBadge size="small" tone="brand">
                  {displayKeys.size}
                </CountBadge> : <span className="minWidth-xeuugli overflow-xb3r6kr textOverflow-xlyipyv whiteSpace-xuxw1ft" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1467">
                  {hasSelection ? findLabel(sections, [...displayKeys][0]) ?? placeholder : placeholder}
                </span>}
            </span>
          </OverlayTriggerView>}
      </button>
      {triggerState.isOpen && !disabled && <Popover layout="triggerMinWidth" placement="bottom start" shouldUpdatePosition={false} state={triggerState} triggerRef={triggerRef} modal={false}>
          <MenuLevel sections={sections} ctx={ctx} label={label ?? 'select'} menuAriaProps={menuProps} searchable={props.searchable} loading={loading} onSearch={onSearch} onLoadMore={onLoadMore} />
          {footerElement != null &&
      // Inside data-select-content so footer interactions don't count as
      // an outside click (which would close the menu).
      <div data-select-content="" className="minHeight-x1ba4aug alignContent-xc26acl paddingInline-x1yzrijw paddingBlock-xlugeym borderTopWidth-x178xt8z borderTopStyle-x13fuv20 borderTopColor-x1frsdck" data-style-src="wino-gc:src/resources/selection-and-input/Select.tsx:1620">
              {footerElement}
            </div>}
        </Popover>}
    </div>;
}

// MARK: style
// ===========
const menuItemStyleProps = (opts: {
  focused: boolean;
  selected: boolean;
  multiple: boolean;
  pressed: boolean;
  disabled: boolean;
  defaultCursor: boolean;
}) => stylex.props(styles.item_root, opts.multiple ? styles.item_gapMultiple : styles.item_gapSingle, opts.focused && (opts.selected ? styles.item_focused : styles.item_focusedNeutral), opts.pressed && styles.item_pressed, opts.disabled && styles.item_disabled, opts.defaultCursor && styles.defaultCursor);
const styles = {
  item_root: {
    "position-kVAEAm": "position-x1n2onr6",
    "display-k1xSpc": "display-x78zum5",
    "boxSizing-kB7OPa": "boxSizing-x9f619",
    "minHeight-kAzted": "minHeight-x1ba4aug",
    "alignItems-kGNEyG": "alignItems-x6s0dn4",
    "borderRadius-kaIpWk": "borderRadius-x1qd5jr3",
    "cursor-kkrTdU": "cursor-x1ypdohk",
    "outline-kI3sdo": "outline-x1a2a7pz",
    "transition-kmkexE": "transition-x1hgg8ub",
    "color-kMwMTN": "color-x1w1nkkj",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1513"
  },
  item_focused: {
    "backgroundColor-kWkggS": "backgroundColor-x1s5ep7y",
    "color-kMwMTN": "color-xsqyr71",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1525"
  },
  item_gapSingle: {
    "gap-kOIVth": "gap-xt0mmm2",
    "paddingInline-kg3NbH": "paddingInline-xq8m5sq",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1529"
  },
  item_gapMultiple: {
    "gap-kOIVth": "gap-xqcaodp",
    "paddingLeft-kE3dHu": "paddingLeft-x1vvg5pe",
    "paddingRight-kpe85a": "paddingRight-xzszh79",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1533"
  },
  item_focusedNeutral: {
    "backgroundColor-kWkggS": "backgroundColor-x9m8vy9",
    "color-kMwMTN": "color-xsqyr71",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1538"
  },
  item_pressed: {
    "backgroundColor-kWkggS": "backgroundColor-x1ybj0jp",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1542"
  },
  item_disabled: {
    "color-kMwMTN": "color-xyemlsm",
    "cursor-kkrTdU": "cursor-x1h6gzvc",
    "backgroundColor-kWkggS": "backgroundColor-xjbqb8w",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1545"
  },
  defaultCursor: {
    "cursor-kkrTdU": "cursor-xt0e3qv",
    $$css: "wino-gc:src/resources/selection-and-input/Select.tsx:1653"
  }
};